import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Lesson } from '@/data/lessons';
import { describeMidi, getNote, type NoteDef } from '@/lib/music/notes';
import { isOnLine } from '@/lib/music/staff';
import { playMidi } from '@/lib/audio/synth';
import {
  applyResult,
  createScore,
  registerNoteResult,
  shuffle,
  type Exercise,
  type NoteStatsMap,
  type ScoreState,
} from '@/lib/exercise/engine';
import {
  MASTERY_ACCURACY_GOAL,
  MASTERY_MIN_ATTEMPTS,
  STREAK_GOAL,
} from '@/lib/progress/gating';

export interface SessionFeedback {
  type: 'correct' | 'wrong';
  message: string;
}

export type SessionPhase = 'intro' | 'practice' | 'done';

interface UseExerciseSessionOptions {
  noteStats?: NoteStatsMap;
  onNoteResult?: (noteId: string, correct: boolean) => void;
  onFinish?: (score: ScoreState) => void;
  /** chamado quando a prática é reiniciada com respostas já registradas */
  onAbort?: (score: ScoreState) => void;
}

function buildGuidance(answerMidi: number, target: NoteDef): string {
  const info = describeMidi(answerMidi);
  const answerLetter = info.label.replace(/[0-9#]/g, '');
  if (answerLetter === target.ptName) {
    return `Quase! Você tocou ${info.label}. O ${target.ptName} precisa ser o ${target.label} — atenção à oitava.`;
  }
  return `Quase! A nota era ${target.label}. Encontre o ${target.label} no teclado para continuar.`;
}

/**
 * Cuida de toda a lógica de uma sessão de reconhecimento de notas.
 *
 * Regra de domínio: repetição até atingir pelo menos 98% de precisão após uma
 * amostra mínima de variações, com todas as notas da aula já apresentadas.
 * O combo de acertos continua existindo como elemento de jogo, mas não é o
 * critério de desbloqueio. Um erro reduz a precisão e o sistema segue gerando
 * novas variações até o aluno recuperar o domínio.
 */
export function useExerciseSession(lesson: Lesson, options: UseExerciseSessionOptions = {}) {
  const requiredNotes = useMemo(() => lesson.notes ?? [], [lesson.notes]);

  const [phase, setPhase] = useState<SessionPhase>('intro');
  const [exercise, setExerciseState] = useState<Exercise | null>(null);
  const [score, setScoreState] = useState<ScoreState>(() => createScore());
  const [streak, setStreak] = useState(0);
  const [seenNotes, setSeenNotes] = useState<string[]>([]);
  const [feedback, setFeedback] = useState<SessionFeedback | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const [lastAnswerMidi, setLastAnswerMidi] = useState<number | null>(null);

  const exerciseRef = useRef<Exercise | null>(null);
  const scoreRef = useRef<ScoreState>(score);
  const statsRef = useRef<NoteStatsMap>({ ...(options.noteStats ?? {}) });
  const streakRef = useRef(0);
  const seenRef = useRef<string[]>([]);
  const bagRef = useRef<string[]>([]);
  const lastNoteRef = useRef<string | null>(null);
  const lockRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  const phaseRef = useRef<SessionPhase>('intro');
  const onNoteResultRef = useRef(options.onNoteResult);
  const onFinishRef = useRef(options.onFinish);
  const onAbortRef = useRef(options.onAbort);

  useEffect(() => {
    onNoteResultRef.current = options.onNoteResult;
    onFinishRef.current = options.onFinish;
    onAbortRef.current = options.onAbort;
  });

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearTimer(), [clearTimer]);

  /**
   * Sorteio equilibrado: uma sacola embaralhada com todas as notas da aula é
   * consumida uma a uma; só quando esvazia, é reembaralhada. Assim, dentro de
   * qualquer janela de 20 respostas todas as notas obrigatoriamente aparecem.
   */
  const drawNoteId = useCallback((): string => {
    const pool = requiredNotes.length > 0 ? requiredNotes : ['C4'];
    if (bagRef.current.length === 0) {
      const bag = shuffle(pool);
      if (bag.length > 1 && bag[bag.length - 1] === lastNoteRef.current) {
        const first = bag.shift();
        if (first) bag.push(first);
      }
      bagRef.current = bag;
    }
    const id = bagRef.current.pop() ?? pool[0];
    lastNoteRef.current = id;
    return id;
  }, [requiredNotes]);

  const buildNext = useCallback((): Exercise => {
    const noteId = drawNoteId();
    const note = getNote(noteId);
    if (lesson.mode === 'line-space') {
      return { kind: 'line-space', noteId, answer: isOnLine(note.step) ? 'line' : 'space' };
    }
    return {
      kind: 'note',
      noteId,
      emphasizedMidi: lesson.id === 1 ? note.midi : undefined,
    };
  }, [drawNoteId, lesson.id, lesson.mode]);

  const finish = useCallback(() => {
    setPhase('done');
    exerciseRef.current = null;
    setExerciseState(null);
    onFinishRef.current?.(scoreRef.current);
  }, []);

  const advance = useCallback(() => {
    lockRef.current = false;
    setFeedback(null);
    setLastAnswerMidi(null);
    const next = buildNext();
    exerciseRef.current = next;
    setExerciseState(next);
  }, [buildNext]);

  const finalize = useCallback(
    (correct: boolean, noteId: string, wrongMessage: string) => {
      const nextScore = applyResult(scoreRef.current, correct);
      scoreRef.current = nextScore;
      setScoreState(nextScore);
      statsRef.current = registerNoteResult(statsRef.current, noteId, correct);
      onNoteResultRef.current?.(noteId, correct);

      if (!correct) {
        streakRef.current = 0;
        setStreak(0);
        setLastError(wrongMessage);
        setFeedback({ type: 'wrong', message: wrongMessage });
        timerRef.current = window.setTimeout(advance, 2400);
        return;
      }

      const nextStreak = streakRef.current + 1;
      streakRef.current = nextStreak;
      setStreak(nextStreak);
      const nextSeen = seenRef.current.includes(noteId)
        ? seenRef.current
        : [...seenRef.current, noteId];
      seenRef.current = nextSeen;
      setSeenNotes(nextSeen);
      setLastError(null);

      const allNotesSeen = requiredNotes.every((id) => nextSeen.includes(id));
      const nextAccuracy =
        nextScore.total > 0 ? Math.round((nextScore.correct / nextScore.total) * 100) : 0;
      const enoughPractice = nextScore.total >= MASTERY_MIN_ATTEMPTS;
      const masteredNow =
        enoughPractice && nextAccuracy >= MASTERY_ACCURACY_GOAL && allNotesSeen;

      setFeedback({
        type: 'correct',
        message: masteredNow
          ? `Domínio alcançado: ${nextAccuracy}% de precisão. Próxima etapa liberada!`
          : `Isso! ${getNote(noteId).label} está certa. Precisão: ${nextAccuracy}% · ${nextScore.total}/${MASTERY_MIN_ATTEMPTS} variações mínimas · combo ${nextStreak}.`,
      });

      if (masteredNow) {
        timerRef.current = window.setTimeout(finish, 950);
        return;
      }
      timerRef.current = window.setTimeout(advance, 950);
    },
    [advance, finish, requiredNotes],
  );

  const startPractice = useCallback(() => {
    clearTimer();
    if (phaseRef.current === 'practice' && scoreRef.current.total > 0) {
      onAbortRef.current?.(scoreRef.current);
    }
    scoreRef.current = createScore();
    setScoreState(scoreRef.current);
    streakRef.current = 0;
    setStreak(0);
    seenRef.current = [];
    setSeenNotes([]);
    bagRef.current = [];
    lastNoteRef.current = null;
    lockRef.current = false;
    setFeedback(null);
    setLastError(null);
    setLastAnswerMidi(null);
    setPhase('practice');
    const first = buildNext();
    exerciseRef.current = first;
    setExerciseState(first);
  }, [buildNext, clearTimer]);

  const submitNote = useCallback(
    (midi: number) => {
      const current = exerciseRef.current;
      if (!current || current.kind !== 'note' || lockRef.current) return;
      lockRef.current = true;
      setLastAnswerMidi(midi);
      const target = getNote(current.noteId);
      const correct = midi === target.midi;
      finalize(correct, current.noteId, buildGuidance(midi, target));
    },
    [finalize],
  );

  const submitLineSpace = useCallback(
    (answer: 'line' | 'space') => {
      const current = exerciseRef.current;
      if (!current || current.kind !== 'line-space' || lockRef.current) return;
      lockRef.current = true;
      const note = getNote(current.noteId);
      const correct = answer === current.answer;
      finalize(
        correct,
        current.noteId,
        `Atenção: ${note.label} está ${
          current.answer === 'line' ? 'em uma linha' : 'em um espaço'
        } da pauta.`,
      );
    },
    [finalize],
  );

  const hearCurrent = useCallback(() => {
    const current = exerciseRef.current;
    if (!current) return;
    playMidi(getNote(current.noteId).midi, 1.1);
  }, []);

  const retry = useCallback(() => {
    clearTimer();
    lockRef.current = false;
    setFeedback(null);
    setLastAnswerMidi(null);
  }, [clearTimer]);

  const timeout = useCallback(() => {
    const current = exerciseRef.current;
    if (!current || current.kind !== 'note' || lockRef.current) return;
    lockRef.current = true;
    setLastAnswerMidi(null);
    const note = getNote(current.noteId);
    finalize(false, current.noteId, `Tempo esgotado! A nota era ${note.label}.`);
  }, [finalize]);

  const skip = useCallback(() => {
    if (phase !== 'practice' || lockRef.current) return;
    clearTimer();
    advance();
  }, [advance, clearTimer, phase]);

  const reset = useCallback(() => {
    clearTimer();
    if (phaseRef.current === 'practice' && scoreRef.current.total > 0) {
      onAbortRef.current?.(scoreRef.current);
    }
    setPhase('intro');
    exerciseRef.current = null;
    setExerciseState(null);
    scoreRef.current = createScore();
    setScoreState(scoreRef.current);
    streakRef.current = 0;
    setStreak(0);
    seenRef.current = [];
    setSeenNotes([]);
    bagRef.current = [];
    lastNoteRef.current = null;
    setFeedback(null);
    setLastError(null);
    setLastAnswerMidi(null);
    lockRef.current = false;
  }, [clearTimer]);

  const accuracy =
    score.total > 0 ? Math.round((score.correct / score.total) * 100) : 0;

  const missingNotes = useMemo(
    () => requiredNotes.filter((id) => !seenNotes.includes(id)),
    [requiredNotes, seenNotes],
  );

  return {
    phase,
    exercise,
    score,
    accuracy,
    streak,
    streakGoal: STREAK_GOAL,
    seenNotes,
    requiredNotes,
    missingNotes,
    feedback,
    lastError,
    lastAnswerMidi,
    startPractice,
    submitNote,
    submitLineSpace,
    hearCurrent,
    retry,
    timeout,
    skip,
    reset,
  };
}